import { Col, Row } from 'antd'
import { Card, Icon, LoadingSpinner, Modal, Toggle, Tooltip } from 'pepsico-ds'
import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from "react-redux";
import { trackModalView, getModalAttributes } from '../../utils/fullstory-utils';
import { urlConstants } from '../../constants/urlConstants';
import CustomButtons from '../Cards/CustomButtons';
import { localStoreKeys } from '../../constants/localStoreKeys';
import { formatNumberWithCommas, splitValueWithUnit, returnHyphenIfNull, splitNumericValue } from '../../utils/utils';
import ModalFilters from './ModalFilters';
import { appNames } from '../../constants/appNames';
import DoubleLineChart from '../Charts/MyRISChart/DoubleLineChart';
import LegendList from '../Charts/Legend/LegendList';
import NoData from '../NoData/NoData';
import BulletChart from '../Charts/MyRISChart/BulletChart';
import PRToPOBarChart from '../Charts/MyRISChart/PRToPOBarChart';
import SpendDonutChart from '../Charts/SpendAnalyticsChart/SpendDonutChart';
import SpendStackedBarChart from '../Charts/SpendAnalyticsChart/SpendStackedBarChart';
import MyRISOpexTable from '../Table/MyRISOpexTable';
import { useToast } from '../../components/Toast/ToastContext';
import Vector from '../../assets/Vector.png'
import DropdownWithIcons from './DropdownWithIcons';
import ExcelJS from 'exceljs';
import { CENTERED, downloadWorkbook, setRowValues } from '../../utils/excelExport';
import InfoIcon from '../Icons/InfoIcon';
import { staticNotes } from '../../constants/staticNotes';
import EDF_checked from "../../../src/assets/EDF_checked.svg";
const MODAL_TITLE = 'R2P Insights';



function MyRISModal({ openModal, setOpenModal, userData, selectedYear, capability, userDataLandingPage, landingPageFilters, appName }) {
    const [myRISData, setMyRISData] = useState({})
    const [loader, setLoader] = useState(true)
    const [buttonGroupItems, setButtonGroupItems] = useState([])
    const feedbackFormUrl = useSelector((state) => state?.localData?.data[localStoreKeys.feedbackFormUrl]);
    const isLoading = useSelector((state) => state?.loader?.isLoading);
    const [dataLoadActionType, setDataLoadActionType] = useState("");

    const touchlessPOLegends= [
        {
            "name": "Touchless PO Count %",
            "color": "#33ABDA"
        },
        {
            "name": "Touchless PO Spend %",
            "color": "#B05788"
        }
    ];
    const catalogueLeakageLegends= [
        {
            "name": "PO Count %",
            "color": "#33ABDA"
        },
        {
            "name": "Spend %",
            "color": "#B05788"
        }
    ]
    const tacticalSavingsLegends= [
        {
            "name": "Savings",
            "color": "#33ABDA"
        },
        {
            "name": "AOP",
            "color": "hsla(0, 0%, 82%, 1)"
        }
    ]

    
    const getMyRISDataFromFilters = (value) => {
        setMyRISData(value.data)
        setLoader(value.loader);
        setDataLoadActionType(value?.data?.type)
        const appList = value.data?.apps
        if (value.modalName == "myRIS" && appList && appList.length > 0) {
            const tempbuttonGroupItems =[
                ...appList
                ?.filter(item => item.isActive)
                .map(item => ({
                    label: item.app,
                    onClick: () => window.open(item.url, "_blank"),
                }))];

            setButtonGroupItems(tempbuttonGroupItems)
        }
    }
    const handleClose = () => {
        try {
            trackModalView(`${MODAL_TITLE} ${capability}`, 'close');
        } catch (error) {
            // Silent fail for tracking errors
        }
        setDataLoadActionType("");
        setOpenModal(false);
    };

    const spend = myRISData?.myRisHeaderResponse?.totalSpend;
    const spendValue = splitValueWithUnit(spend)?.['numericValue'];
    const spendUnit = splitValueWithUnit(spend)?.['unit'];
    const capEx = myRISData?.myRisHeaderResponse?.capExSpend;
    const capExPercent = myRISData?.myRisHeaderResponse?.capExPercentage;
    const opEx =  myRISData?.myRisHeaderResponse?.opexSpend;
    const opExPercent = myRISData?.myRisHeaderResponse?.opexPercentage;
    const catalogueValue = myRISData?.myRisHeaderResponse?.catalogSpend;
    const cataloguePercent = myRISData?.myRisHeaderResponse?.catalogPercentage;
    const nonCatalogueValue = myRISData?.myRisHeaderResponse?.nonCatalogSpend;
    const nonCataloguePercent = myRISData?.myRisHeaderResponse?.nonCatalogPercentage;
    const tacticalSavings = myRISData?.myRisHeaderResponse?.tacticalSaving;
    const tacticalSavingsValue = splitValueWithUnit(tacticalSavings)?.['numericValue'];
    const tacticalSavingsUnit = splitValueWithUnit(tacticalSavings)?.['unit'];
    const actualSavings =  myRISData?.myRisHeaderResponse?.actualSaving;
    const actualSavingsValue = splitValueWithUnit(actualSavings)?.['numericValue'];
    const actualSavingsUnit = splitValueWithUnit(actualSavings)?.['unit'];
    const actualSavingsPercent = myRISData?.myRisHeaderResponse?.actualPercentage;
    const aopValue = myRISData?.myRisHeaderResponse?.aopSaving;
    const aopPercent = 20;
    const missedSavings = myRISData?.myRisHeaderResponse?.missedSaving;
    const missedSavingsValue = splitValueWithUnit(missedSavings)?.['numericValue'];
    const missedSavingsUnit = splitValueWithUnit(missedSavings)?.['unit'];
    const volume = myRISData?.myRisHeaderResponse?.volume;
    const isEDFIntegrated = myRISData?.edfintegrated;

    const touchlessPOSpend = myRISData?.touchLessPOChartDetails?.totalSpend;
    const touchlessPOSpendValue = splitValueWithUnit(touchlessPOSpend)?.['numericValue'];
    const touchlessPOSpendsUnit = splitValueWithUnit(touchlessPOSpend)?.['unit'];
    const touchlessPOVolume = myRISData?.touchLessPOChartDetails?.totalVolume;
    const touchlessPOVolumeValue = splitNumericValue(touchlessPOVolume)?.['value'];
    const touchlessPOVolumesUnit = splitNumericValue(touchlessPOVolume)?.['suffix'];
    const touchlessPOData = myRISData?.touchLessPOChartDetails?.touchLessPODetails;
    const opexTableData = myRISData?.opexTableData;
    const opexTableHeader = myRISData?.opexTableTitle;
    const opexTableTitle = myRISData?.opexTableHeader;
    const excludeKeys = ["key", "section", "parent", "kpi"];

    const opexSectorData = opexTableData && Object.keys(opexTableData[0]).filter(key => !excludeKeys.includes(key));
    

    const catalogueLeakageSpend = myRISData?.suspectedCatalogueChartDetails?.totalSpend;
    const catalogueLeakageSpendValue = splitValueWithUnit(catalogueLeakageSpend)?.['numericValue'];
    const catalogueLeakageSpendUnit = splitValueWithUnit(catalogueLeakageSpend)?.['unit'];
    const catalogueLeakageVolume =  myRISData?.suspectedCatalogueChartDetails?.totalVolume;
    const catalogueLeakageVolumeValue = splitNumericValue(catalogueLeakageVolume)?.['value'];
    const catalogueLeakageVolumeUnit = splitNumericValue(catalogueLeakageVolume)?.['suffix'];
    const catalogueLeakageSpendPercent = myRISData?.suspectedCatalogueChartDetails?.prctSpend
    const catalogueLeakageVolumePercent = myRISData?.suspectedCatalogueChartDetails?.prctVolume
    const catalogueLeakageData = myRISData?.suspectedCatalogueChartDetails?.spendAndVolumeDetails;
    
    const tacticalSavingsData =myRISData?.savingsAndAopData;
    const savingsTransformedData = tacticalSavingsData?.map(item => ({
        name: item.sectorOrBu,
        innerValue: item.savings?item.savings:0.0000001,
        outerValue: item.aop?item.aop:0.0000001
    }));
    
    const spendByBUData = myRISData?.myRisPieChartResponse;
    const sectorBuColor = myRISData?.sectorBUColor
    let transformedBUData = spendByBUData?.map(item => ({
        category: item.sector,
        name: item.bu,
        color: item.colorCode?item.colorCode:"#000000",
        sectorColor: item.sectorColorCode?item.sectorColorCode:"#000000",
        value: Number(item.spend)
      })); 
    const uniqueSectorValuesForBUChart = new Set(transformedBUData?.map(item => item.category));
    const multipleSectorFlagForBUChart = uniqueSectorValuesForBUChart?.size > 1;

    const categoryColorMapping = myRISData?.spendByCategoryColor;
    const spendAtSectorAndCategoryL1Data = myRISData?.spendByCategoryResponse;
    
    const l1CatgoryColorsData = spendAtSectorAndCategoryL1Data?.flatMap(item =>
        categoryColorMapping?.map(style => ({
          category: item.category,
          name: style.name,
          color: style.color
        }))
      );

    let prToPOData = myRISData?.prToPOData;
    prToPOData = prToPOData?.map(item=>{
        const fillColor = item?.name==="UNCLASSIFIED"?"hsla(29, 100%, 55%, 1)":"#0078A7";
        return {...item, fill:fillColor};
    }
    );
    

    const myRISModalRef = useRef();
    const gRSpendbySectorBURef = useRef();
    const tacticalSavingbySectorRef = useRef();
    const touchlessPOVolPOSpendRef = useRef();
    const suspectedCatalogLeakageRef = useRef();
    const stackedBarRef = useRef();
    const lineBarRef = useRef();
    const opexTableRef = useRef();

    const { showToast } = useToast();


      
    const downloadExcel = async () => {
        const excelTitle = opexTableHeader || "";
      
        // 1. Collect keys
        const allKeys = Array.from(new Set((opexTableData || []).flatMap(obj => Object.keys(obj))));
        const filteredKeys = allKeys.filter(k => k !== "key");
      
        // 2. Order columns (section + parent + rest)
        const baseOrder = ["section", "parent", "kpi"];
        const restKeys = filteredKeys.filter(k => !baseOrder.includes(k));
        const orderedKeys = [...baseOrder, ...restKeys];
      
        // 3. Header row (only DESCRIPTION, KPI, GLOBAL...)
        const headerRow = ["DESCRIPTION", "", "KPI", ...restKeys.map(k => k.toUpperCase())];
      
        // 4. Data rows: keep section + parent separate
        const dataRows = (opexTableData || []).map(item => {
          return [
            item.section || "",
            item.parent || "",
            item.kpi || "",
            ...restKeys.map(k => item[k] ?? "")
          ];
        });
      
        // 5. Build AOA
        const aoa = [
          [excelTitle, ...Array(headerRow.length - 1).fill("")], // Row 0 = title
          headerRow,                                             // Row 1 = DESCRIPTION, KPI, GLOBAL...
          ...dataRows                                            // Row 2+ = data
        ];
      
        // 6. Create worksheet
        const workbook = new ExcelJS.Workbook();
        const ws = workbook.addWorksheet("Sheet1");
        ws.addRows(aoa.slice(0, 2));
        dataRows.forEach(values => setRowValues(ws.addRow([]), values));

        // 7. Merge title across all columns
        ws.mergeCells(1, 1, 1, headerRow.length);

        // 8. Merge DESCRIPTION across Section + Parent
        ws.mergeCells(2, 1, 2, 2);

        // 9. Style title (Row 1)
        const titleCell = ws.getCell(1, 1);
        titleCell.font = { bold: true, size: 16, name: "Arial" };
        titleCell.alignment = CENTERED;

        // 10. Style header row (Row 2)
        headerRow.forEach((h, colIndex) => {
          if (h) { // skip the empty cell inside merged DESCRIPTION
            const cell = ws.getCell(2, colIndex + 1);
            cell.font = { bold: true, size: 11, name: "Arial" };
            cell.alignment = CENTERED;
          }
        });

        // 11. Export workbook
        await downloadWorkbook(workbook, `${opexTableHeader}.xlsx`);
      };
      
    
    
    
      
    
    const checkPDFDownloaded = (isPdfDownloaded, isModal, fileURL, isExcel, isTable, isJpeg) => {
        if(isPdfDownloaded){
            if(isModal){
                showToast('success', !isJpeg ? "PDF of full page successfully downloaded." : 'JPEG of full page successfully downloaded.', {
                    icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
                });
            } else if(isTable){
                showToast('success', !isJpeg ? "PDF of a table successfully downloaded." : 'JPEG of a table successfully downloaded.', {
                    icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
                });
            }
            else{
                showToast('success', !isJpeg ? "PDF of a chart successfully downloaded.": 'JPEG of a chart successfully downloaded.', {
                    icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
                });
            }
        }
        else if(isExcel){
            downloadExcel()
        }
        else{
            showToast('Error', "Failed to download. Please try again.", {
                icon: <img src={Vector} alt='image' style={{ width: '20px', height: '20px' }} />
            });
        }   
    }   

    
    return (
        <Modal
            closeOnOutsideClick={false}
            onCloseModal={handleClose}
            primaryButtonProps={{
                onClick: handleClose,
                size: "medium",
                text: "Close",
                variant: "primary",
            }}
            showPrimaryButton={true}
            showLink={false}
            showSecondaryButton={false}
            showTertiaryButton={false}
            showAuxiliaryButton={false}
            showSlot={true}
            showModal={openModal}
            hideCloseButton={false}
            type="XL"
            {...getModalAttributes(`${MODAL_TITLE} ${capability}`)}
        >
            <div ref={myRISModalRef}>
                <div className="modal-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>
                    {userData.FirstName}, indirect spend through myBUY for your category is <b>{spend ? spend : 0}</b>
                    {
                        isEDFIntegrated &&
                        <Tooltip direction="bottom" text={staticNotes.EDF_NOTE}>
                            <img src={EDF_checked} alt='image' style={{ width: '18px', height: '22px', marginLeft: "8px", overflow:"visible", marginBottom: "7px" }} />
                        </Tooltip>
                    }
                </span>        
                    {
                        isLoading ? (
                            <></>
                        ) :                    
                        <>
                            <span style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                <CustomButtons isModal={true} appList={myRISData?.apps} />
                                <span style={{float: 'right', position: 'relative', right: '2rem', bottom: '0.6rem'}}>
                                    <DropdownWithIcons checkPDFDownloaded={checkPDFDownloaded} modalRef={myRISModalRef} kpiName={"MyRIS_Modal"} isModal={true}/>
                                </span>
                            </span>


                           
                        </>
                    }
                </div>
                {/* {(myRISData?.lastUpdate) && (<div className="modal-update-frequesncy">Data As On: {returnHyphenIfNull(myRISData?.lastUpdate)} | Refresh Frequency: {returnHyphenIfNull(myRISData?.refreshFrequency)}</div>)}
                <ModalFilters modalName={"MyRIS"} selectedYear={selectedYear} capability={capability} userData={userDataLandingPage} onParentCallback={getMyRISDataFromFilters} landingPageFilters={landingPageFilters} setLoader={setLoader} appName={appName} />
                <br />
                {
                    isLoading ? (
                        <></>
                    ) :                    
                    <>
                        <CustomButtons isModal={true} appList={myRISData?.apps} />
                    </>
                } */}
            {/* </div> */}
            <div className="modal-update-frequesncy" style={{ marginBottom: "5px", display: "flex" }}><Icon alt="" icon="info" size="small" /> &nbsp; {staticNotes.MYRIS_OPEX_NOTE}</div>
            {(myRISData?.lastUpdate) && (<div className="modal-update-frequesncy">Data As On: {returnHyphenIfNull(myRISData?.lastUpdate)} | Refresh Frequency: {returnHyphenIfNull(myRISData?.refreshFrequency)}</div>)}
            <ModalFilters modalName={"myRIS"} selectedYear={selectedYear} capability={capability} userData={userDataLandingPage} onParentCallback={getMyRISDataFromFilters} landingPageFilters={landingPageFilters} setLoader={setLoader} appName={appName} />
            <br />
            {
                (loader || isLoading) ?
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: 300 }}>
                        <LoadingSpinner size={{ height: '80px', width: '80px' }} />
                    </div>
                    :
                    <>
                        <div style={{ marginTop: 10, marginBottom: 10, display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                                <Row gutter={[10, 10]} justify="start" style={{ width: "100%" }}>
                                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                        <Card style={{ height: '100%',display:'flex' }}>
                                            <div className='header-kpi-wrapper'>
                                                {
                                                    spend !== undefined || spend !== null ?
                                                        <>
                                                            <Col xs={24} sm={24} md={4} lg={4} xl={4}>
                                                                <div>
                                                                    <div className= "spend-modal-alphanumeric-value">
                                                                        <div className='spend-modal-numeric-value'>{formatNumberWithCommas(spendValue) || 0}</div>
                                                                        <div className="spend-modal-alpha-value" style={{ display: "flex" }}>{spendUnit}</div>
                                                                    </div>
                                                                    <div className='archlet-modal-header-kpi-description' style={{ display: "flex" }}>
                                                                        Indirect Spend through myBUY
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </>
                                                        : ''
                                                }

                                                {/* CapEx OpEx*/}
                                                {
                                                    capEx !== undefined || capEx !== null ?
                                                        <>
                                                            <div className="archlet-modal-divider-verticle" />
                                                            <Col xs={24} sm={24} md={2} lg={2} xl={2}>
                                                                <div>
                                                                    <div className= "spend-modal-alphanumeric-value">
                                                                        <div className='spend-modal-numeric-value' style={{ fontSize: "18px" }}>{capEx || 0}</div>
                                                                    </div>
                                                                    <div className='archlet-modal-header-kpi-description'>
                                                                        <div>CapEx</div>
                                                                        <div>({capExPercent || 0}%)</div>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </>
                                                        : ''
                                                }
                                                {
                                                    opEx !== undefined || opEx !== null ?
                                                        <>
                                                            <Col xs={24} sm={24} md={2} lg={2} xl={2}>
                                                                <div>
                                                                    <div className= "spend-modal-alphanumeric-value">
                                                                        <div className='spend-modal-numeric-value' style={{ fontSize: "18px" }}>{opEx || 0}</div>
                                                                    </div>
                                                                    <div className='archlet-modal-header-kpi-description'>
                                                                        <div>OpEx</div>
                                                                        <div>({opExPercent || 0}%)</div>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </>
                                                        : ''
                                                }

                                                {/* catalogue non-catalogue*/}
                                                {
                                                    catalogueValue !== undefined || catalogueValue !== null ?
                                                        <>
                                                            <div className="archlet-modal-divider-verticle" />
                                                            <Col xs={24} sm={24} md={2} lg={2} xl={2}>
                                                                <div>
                                                                    <div className= "spend-modal-alphanumeric-value">
                                                                        <div className='spend-modal-numeric-value' style={{ fontSize: "18px" }}>{catalogueValue || 0}</div>
                                                                    </div>
                                                                    <div className='archlet-modal-header-kpi-description'>
                                                                        <div>Catalog</div>
                                                                        <div>({cataloguePercent || 0}%)</div>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </>
                                                        : ''
                                                }
                                                {
                                                    nonCatalogueValue !== undefined || nonCatalogueValue !== null ?
                                                        <>
                                                            <Col xs={24} sm={24} md={2} lg={2} xl={2}>
                                                                <div>
                                                                    <div className= "spend-modal-alphanumeric-value">
                                                                        <div className='spend-modal-numeric-value' style={{ fontSize: "18px" }}>{nonCatalogueValue || 0}</div>
                                                                    </div>
                                                                    <div className='archlet-modal-header-kpi-description'>
                                                                        <div>Non-Catalog</div>
                                                                        <div>({nonCataloguePercent || 0}%)</div>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </>
                                                        : ''
                                                }

                                                {/* tactical savings */}
                                                {/* {
                                                    tacticalSavings !== undefined || tacticalSavings !== null ?
                                                        <>
                                                            <div className="archlet-modal-divider-verticle" />
                                                            <Col xs={24} sm={24} md={3} lg={3} xl={3}>
                                                                <div>
                                                                    <div className= "spend-modal-alphanumeric-value">
                                                                        <div className='spend-modal-numeric-value'>{formatNumberWithCommas(tacticalSavingsValue) || 0}</div>
                                                                        <div className="spend-modal-alpha-value" style={{ display: "flex" }}>{tacticalSavingsUnit}</div>
                                                                    </div>
                                                                    <div className='archlet-modal-header-kpi-description' style={{ display: "flex" }}>
                                                                        Total Tactical Savings
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </>
                                                        : ''
                                                } */}

                                                {/* actual aop*/}
                                                {
                                                    actualSavings !== undefined || actualSavings !== null ?
                                                        <>
                                                            <div className="archlet-modal-divider-verticle" style={{marginLeft:"16px"}}/>
                                                            <Col xs={24} sm={24} md={3} lg={3} xl={3}>
                                                                <div>
                                                                    <div className= "spend-modal-alphanumeric-value">
                                                                        <div className='spend-modal-numeric-value'>{formatNumberWithCommas(actualSavingsValue) || 0}</div>
                                                                        <div className="spend-modal-alpha-value" style={{ display: "flex" }}>{actualSavingsUnit}</div>
                                                                    </div>
                                                                    <div className='archlet-modal-header-kpi-description'>
                                                                        <div>Actual Savings</div>
                                                                        <div>({actualSavingsPercent || 0}% of AOP)</div>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </>
                                                        : ''
                                                }
                                                {/* {
                                                    aopValue !== undefined || aopValue !== null ?
                                                        <>
                                                            <Col xs={24} sm={24} md={2} lg={2} xl={2}>
                                                                <div>
                                                                    <div className= "spend-modal-alphanumeric-value">
                                                                        <div className='spend-modal-numeric-value' style={{ fontSize: "18px" }}>{aopValue || 0}</div>
                                                                    </div>
                                                                    <div className='archlet-modal-header-kpi-description'>
                                                                        <div>AOP</div>
                                                                        <div>({aopPercent || 0}%)</div>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </>
                                                        : ''
                                                } */}

                                                {/* missed savings */}
                                                {
                                                    missedSavings !== undefined || missedSavings !== null ?
                                                        <>
                                                            <div className="archlet-modal-divider-verticle" />
                                                            <Col xs={24} sm={24} md={3} lg={3} xl={3}>
                                                                <div>
                                                                    <div className= "spend-modal-alphanumeric-value">
                                                                        <div className='spend-modal-numeric-value'>{formatNumberWithCommas(missedSavingsValue) || 0}</div>
                                                                        <div className="spend-modal-alpha-value" style={{ display: "flex" }}>{missedSavingsUnit}</div>
                                                                    </div>
                                                                    <div className='archlet-modal-header-kpi-description' style={{ display: "flex" }}>
                                                                        Missed Savings
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </>
                                                        : ''
                                                }

                                                
                                                {
                                                    volume !== undefined || volume !== null ?
                                                        <>
                                                            <div className="archlet-modal-divider-verticle" />
                                                            <Col xs={24} sm={24} md={3} lg={3} xl={3}>
                                                                <div style={{ display: 'flex',justifyContent:"space-between" }}>
                                                                    <div>
                                                                        <div className="archlet-modal-alphanumeric-value">
                                                                            <div className="archlet-modal-header-kpi">{volume ? formatNumberWithCommas(volume) : 0}</div>
                                                                        </div>
                                                                        <div className='archlet-modal-header-kpi-description' style={{ display: "flex" }}>PO Count</div>
                                                                    </div>
                                                                    {/* <InfoIcon text="Volume represents the Number of POs" direction='left'  /> */}
                                                                </div>
                                                            </Col>
                                                        </>
                                                        : ''
                                                }
                                            </div>
                                        </Card>
                                    </Col> 
                                </Row>
                        </div>
                        <>
                          
                            <div>
                                    <Row gutter={[10, 10]} justify={"start"} style={{ width: "100%" }}>

                                        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                            <div ref={gRSpendbySectorBURef} style={{height: '100%'}}>
                                                <Card style={{ height: '100%' }}>
                                                    <div className="archlet-chart-title" >GR Spend by Sector and BU
                                                        <span style={{float: 'right'}}>
                                                            <DropdownWithIcons checkPDFDownloaded={checkPDFDownloaded} modalRef={gRSpendbySectorBURef} kpiName={"GR Spend by Sector and BU"}/>
                                                        </span>
                                                    </div>
                                                    {
                                                        transformedBUData && transformedBUData.length>0 ?
                                                            <>
                                                                {
                                                                    <>
                                                                        <SpendDonutChart countryData={transformedBUData} flag={multipleSectorFlagForBUChart} isSpendChart={true} />
                                                                        
                                                                        <br/>
                                                                        <LegendList legends={sectorBuColor} />
                                                                    </>
                                                                }
                                                            </>
                                                            : <NoData />
                                                    }
                                                </Card>
                                            </div>
                                        </Col>
                                        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                            <div ref={tacticalSavingbySectorRef} style={{height: '100%'}}>
                                                <Card style={{ height: '100%' }}>
                                                    <div className="archlet-chart-title" >Tactical Saving by Sector / BU
                                                        <span style={{float: 'right'}}>
                                                            <DropdownWithIcons checkPDFDownloaded={checkPDFDownloaded} modalRef={tacticalSavingbySectorRef} kpiName={"Tactical Saving by Sector / BU"}/>
                                                        </span>
                                                    </div>
                                                    {
                                                        savingsTransformedData?.length > 0 ?
                                                            <>
                                                                <BulletChart data={savingsTransformedData}/>
                                                                <LegendList legends={tacticalSavingsLegends} />
                                                            </> 
                                                            : <NoData />
                                                    }
                                                </Card>
                                            </div>
                                        </Col>
                                        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                            <div ref={touchlessPOVolPOSpendRef} style={{height: '100%'}}>
                                                <Card style={{ height: '100%' }} className="archlet-chart-card">
                                                
                                                    <div className="archlet-chart-title" style={{ width: "100%", textAlign: "left" }}>                                       
                                                        <span>
                                                            Touchless PO Count and PO Spend
                                                            <InfoIcon  style={{position: 'relative', left: '0.5rem'}}
                                                                                    text={staticNotes.MYRIS_TOUCHLESS_PO_NOTE} />
                                                        </span>                        
                                                        <span style={{float: 'right'}}>
                                                            <DropdownWithIcons checkPDFDownloaded={checkPDFDownloaded} modalRef={touchlessPOVolPOSpendRef} kpiName={"Touchless PO Vol and PO Spend"}/>
                                                        </span>
                                                    </div>
                                                    <div className='header-kpi-wrapper'>
                                                        {
                                                            touchlessPOSpend !== undefined || touchlessPOSpend !== null ?
                                                                <>
                                                                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                                                        <div>
                                                                            <div className= "spend-modal-alphanumeric-value">
                                                                                <div className='spend-stats-value' style={{ fontSize: '25px' }}>{formatNumberWithCommas(touchlessPOSpendValue) || 0}</div>
                                                                                <div className="spend-stats-alpha" style={{ display: "flex", fontSize: '12px' }}>{touchlessPOSpendsUnit}</div>
                                                                            </div>
                                                                            <div className='archlet-modal-header-kpi-description' style={{ display: "flex" }}>
                                                                                Total Spend
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                </>
                                                                : ''
                                                        }

                                                        {
                                                            touchlessPOVolume !== undefined || touchlessPOVolume !== null ?
                                                                <>
                                                                    <div className="archlet-modal-divider-verticle" />
                                                                    <Col xs={24} sm={24} md={4} lg={4} xl={4}>
                                                                        <div>
                                                                        <div className= "spend-modal-alphanumeric-value">
                                                                                <div className='spend-stats-value' style={{ fontSize: '25px' }}>{formatNumberWithCommas(touchlessPOVolumeValue) || 0}</div>
                                                                                <div className="spend-stats-alpha" style={{ display: "flex", fontSize: '12px' }}>{touchlessPOVolumesUnit}</div>
                                                                            </div>
                                                                            <div className='archlet-modal-header-kpi-description' style={{ display: "flex" }}>
                                                                                PO Count
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                </>
                                                                : ''
                                                        }
                                                    </div>
                                                    
                                                    {
                                                        touchlessPOData && touchlessPOData?.length > 0 ?
                                                        <>
                                                            <DoubleLineChart data={touchlessPOData} keys={touchlessPOLegends}/>
                                                            <LegendList legends={touchlessPOLegends} />
                                                        </>
                                                        : <NoData />
                                                    }
                                                </Card>
                                            </div>
                                        </Col>

                                        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                            <div ref={suspectedCatalogLeakageRef} style={{height: '100%'}}>
                                                <Card style={{ height: '100%' }}>
                                                    <div className="archlet-chart-title">Suspected Catalog Leakage
                                                        <span style={{float: 'right'}}>
                                                            <DropdownWithIcons checkPDFDownloaded={checkPDFDownloaded} modalRef={suspectedCatalogLeakageRef} kpiName={"Suspected Catalog Leakage"}/>
                                                        </span>
                                                    </div>
                                                    
                                                    <div className='header-kpi-wrapper'>
                                                        {
                                                            catalogueLeakageSpend !== undefined || catalogueLeakageSpend !== null ?
                                                                <>
                                                                    <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                                                                        <div>
                                                                            <div className= "spend-modal-alphanumeric-value">
                                                                                <div className='spend-stats-value' style={{ fontSize: '25px' }}>{formatNumberWithCommas(catalogueLeakageSpendValue) || 0}</div>
                                                                                <div className="spend-stats-alpha" style={{ display: "flex", fontSize: '12px' }}>{catalogueLeakageSpendUnit}</div>
                                                                            </div>
                                                                            <div className='archlet-modal-header-kpi-description' style={{ display: "flex" }}>
                                                                                Spend
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                </>
                                                                : ''
                                                        }
                                                        {
                                                            catalogueLeakageVolume !== undefined || catalogueLeakageVolume !== null ?
                                                                <>
                                                                    <div className="archlet-modal-divider-verticle" />
                                                                    <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                                                                        <div>
                                                                            <div className= "spend-modal-alphanumeric-value">
                                                                                <div className='spend-stats-value' style={{ fontSize: '25px' }}>{formatNumberWithCommas(catalogueLeakageVolumeValue) || 0}</div>
                                                                                <div className="spend-stats-alpha" style={{ display: "flex", fontSize: '12px' }}>{catalogueLeakageVolumeUnit}</div>
                                                                            </div>
                                                                            <div className='archlet-modal-header-kpi-description' style={{ display: "flex" }}>
                                                                                PO Count
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                </>
                                                                : ''
                                                        }

                                                        {
                                                            catalogueLeakageSpendPercent !== undefined || catalogueLeakageSpendPercent !== null ?
                                                                <>
                                                                    <div className="archlet-modal-divider-verticle" />
                                                                    <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                                                                        <div>
                                                                            <div className= "spend-modal-alphanumeric-value">
                                                                                <div className='spend-stats-value' style={{ fontSize: '25px' }}>{formatNumberWithCommas(catalogueLeakageSpendPercent) || 0}</div>
                                                                            </div>
                                                                            <div className='archlet-modal-header-kpi-description' style={{ display: "flex" }}>
                                                                                Spend %
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                </>
                                                                : ''
                                                        }

                                                        {
                                                            catalogueLeakageVolumePercent !== undefined || catalogueLeakageVolumePercent !== null ?
                                                                <>
                                                                    <div className="archlet-modal-divider-verticle" />
                                                                    <Col xs={24} sm={24} md={4} lg={4} xl={4}>
                                                                        <div>
                                                                            <div className= "spend-modal-alphanumeric-value">
                                                                                <div className='spend-stats-value' style={{ fontSize: '25px' }}>{formatNumberWithCommas(catalogueLeakageVolumePercent) || 0}</div>
                                                                            </div>
                                                                            <div className='archlet-modal-header-kpi-description' style={{ display: "flex" }}>
                                                                                PO Count %
                                                                            </div>
                                                                        </div>
                                                                    </Col>
                                                                </>
                                                                : ''
                                                        }
                                                    </div>
                                                    {
                                                        catalogueLeakageData && catalogueLeakageData && catalogueLeakageData.length > 0 ?
                                                        <>
                                                            <DoubleLineChart data={catalogueLeakageData} keys={catalogueLeakageLegends} />
                                                            <LegendList legends={catalogueLeakageLegends} />
                                                        </>
                                                        : <NoData />
                                                    }
                                                </Card>
                                            </div>
                                        </Col>
                                    </Row>
                                    <br />
                                    <Row gutter={[10, 10]} justify={"start"} style={{ width: "100%" }}>
                                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                            <div ref={lineBarRef}>
                                                <Card style={{ height: '100%' }}>
                                                    {
                                                        prToPOData && prToPOData?.length>0 ?
                                                            <>
                                                                <div className="total-spend-xaxis-label" style={{ width: "100%", textAlign: "left", display: "flex", justifyContent:"space-between", color:"#3A3A3A", fontSize: '18px' }}>
                                                                        <span>
                                                                            {"PR to PO TAT (Days)"}
                                                                            {/* <InfoIcon  style={{position: 'relative', left: '0.5rem'}}
                                                                                        text={staticNotes.ARCHLET_BU_NOTE} /> */}
                                                                        </span>
                                                                        <span style={{float: 'right'}}>
                                                                            <DropdownWithIcons checkPDFDownloaded={checkPDFDownloaded} modalRef={lineBarRef} kpiName={"PR to PO TAT (Days)"}/>
                                                                        </span>
                                                                </div>
                                                                <PRToPOBarChart data={prToPOData}/>
                                                            </> 
                                                        :
                                                            <NoData />
                                                    }
                                                </Card>
                                            </div>
                                        </Col>
                                    </Row>
                                    <br />
                                    <Row gutter={[10, 10]} justify={"start"} style={{ width: "100%" }}>
                                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                            <div ref={stackedBarRef}>
                                                <Card style={{ height: '100%' }}>
                                                    {
                                                        spendAtSectorAndCategoryL1Data && spendAtSectorAndCategoryL1Data?.length>0 ?
                                                            <>
                                                            
                                                                <div className="total-spend-xaxis-label" style={{ width: "100%", textAlign: "left", display: "flex", justifyContent:"space-between", color:"#3A3A3A", fontSize: '18px'  }}>
                                                                        <span>{"Spend by Sectors and Categories L1"}</span>
                                                                        <span style={{float: 'right'}}>
                                                                            <DropdownWithIcons checkPDFDownloaded={checkPDFDownloaded} modalRef={stackedBarRef} kpiName={"Spend by Sectors and Categories L1"}/>
                                                                        </span>
                                                                </div>
                                                                
                                                                <SpendStackedBarChart categories={l1CatgoryColorsData} data={spendAtSectorAndCategoryL1Data}/>
                                                                <LegendList legends={categoryColorMapping} />
                                                        </> 
                                                        :
                                                            <NoData />
                                                    }
                                                </Card>
                                            </div>
                                        </Col>
                                    </Row>
                                    <br />
                                    <Row gutter={[10, 10]} justify={"start"} style={{ width: "100%" }}>
                                        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                            <div ref={opexTableRef}>
                                                <Card style={{ height: '100%' }}>
                                                    {
                                                        opexTableData && opexTableData?.length>0 ?
                                                            <>
                                                                <div className="total-spend-xaxis-label" style={{ width: "100%", textAlign: "left", display: "flex", justifyContent:"space-between", color:"#3A3A3A", fontSize: '18px' }}>
                                                                        <span>{opexTableHeader}</span>
                                                                        <span style={{float: 'right'}}>
                                                                            <DropdownWithIcons checkPDFDownloaded={checkPDFDownloaded} modalRef={opexTableRef} kpiName={opexTableHeader} isTable={true}/>
                                                                        </span>
                                                                </div>
                                                                <MyRISOpexTable tableData={opexTableData} sectorData={opexSectorData} opexTableTitle={opexTableTitle} opexTableHeader={opexTableHeader}/>
                                                            </> 
                                                        :
                                                            <NoData />
                                                    }
                                                    
                                                </Card>
                                            </div>
                                        </Col>
                                    </Row>
                                    <br/>

                                    <div className="spend-modal-manage-spend" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", width: '100%'  }}>
                                        {
                                            myRISData?.owners?.length > 0 &&
                                            <div style={{width: '100%'}}>Please reach out to {myRISData?.owners?.map((contact, index) => (
                                                <React.Fragment key={index}>
                                                    <a href={`mailto:${contact.emailId}`} style={{ color: "#005cbc", textDecoration: "None" }}>{contact.emailId}</a>
                                                    {index < myRISData?.owners?.length - 1 && ', '}
                                                </React.Fragment>
                                            ))} for any query on the data displayed.</div>
                                        }
                                        <div style={{width: '100%'}}>Please fill in <a href={feedbackFormUrl} target="_blank" style={{ color: "#005cbc", textDecoration: "None" }}>this form</a> for any feedback related to Procurement Hub.</div>
                                    </div> 
                            </div>            
                        
                        </>
                    </>
                    
                }
                <br/>
            </div>        
        </Modal>
        )
}

export default MyRISModal   